import type { ReactElement, RefObject } from "react";

import { PLACEHOLDER_LABELS } from "@/lib/labels";

/**
 * The placeholders a text may use, as chips under its field: a click puts the token where the
 * caret stands, or over the selection, and the caret after it. The token is what is seen, the
 * meaning comes on hover (`title`) and is what a screen reader hears. The pattern of the mail
 * editors in the customer portal.
 */
export function PlaceholderPicker({
  names,
  field,
  value,
  onChange,
  disabled = false,
}: {
  readonly names: readonly string[];
  /** The text field the token goes into. */
  readonly field: RefObject<HTMLTextAreaElement | null>;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly disabled?: boolean;
}): ReactElement | null {
  if (names.length === 0) {
    return null;
  }
  const insert = (token: string): void => {
    const node = field.current;
    const start = node?.selectionStart ?? value.length;
    const end = node?.selectionEnd ?? value.length;
    const caret = start + token.length;
    onChange(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      node?.focus();
      node?.setSelectionRange(caret, caret);
    });
  };
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {names.map((name) => {
        const token = `{${name}}`;
        const meaning = PLACEHOLDER_LABELS[name] ?? name;
        return (
          <button
            key={name}
            type="button"
            className="rounded-md border px-1.5 py-0.5 font-mono text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            title={meaning}
            aria-label={`${meaning} einfügen`}
            disabled={disabled}
            onClick={() => {
              insert(token);
            }}
          >
            {token}
          </button>
        );
      })}
    </div>
  );
}
